import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _AbDecimal;
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ValueFormatAttributesUtils } from "abweb/dto/ds/ValueFormatAttributesUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { AbBigDecimal } from "abweb/utils/AbBigDecimal";

/**
 * A class that represents numbers as decimals.
 *
 * Addresses issues of exact representation of decimals... binary floating point rounding, 52 bit precision, etc.
 * Used by Metadata Hub to represent decimal database values without loss of information.
 *
 * This is currently just an abstraction. In the future we may need some implementation.
 * - comparisons for sorting
 * - math, although there is so far little need for math. When we need that, we may look at third party packages.
 *
 * For performance, we should optimize as much as possible for decimal values that can be represented exactly as number
 * (i.e. 52 bit integers)
 *
 * Users of AbDecimal should avoid converting these to numbers, as not all AbDecimals can be represented or
 * exactly represented as numbers. Instead, we should create the needed operations on AbDecimal. For short term
 * expediency we may have to, but it would be good to put those number based shortcuts inside of AbDecimal methods.
 * For example, we will need local aware number formatting. Initially we might just convert to number and use existing number
 * formatting.
 */
export class AbDecimal {
  /**
   * Take a standard decimal representation of a number. Converts localized number representations
   * to en_US. Do not use thousands separators.
   */
  constructor(x) {
    // implementation will eventually support other operations, and may keep number based representations internally
    _defineProperty(this, "bigDecimalOrInvalidGarbage", void 0);
    const bigDecimal = AbBigDecimal.from(x);
    if (!bigDecimal.isNaN()) {
      this.bigDecimalOrInvalidGarbage = bigDecimal;
    } else {
      const sep = NumberUtils.getLocaleDecimalSeparator();
      if (sep === ".") {
        this.bigDecimalOrInvalidGarbage = x;
      } else {
        // We have to convert sep's (most likely ",") to "."
        this.bigDecimalOrInvalidGarbage = x.replaceAll(sep, ".");
      }
    }
  }

  /**
   * Returns true if the string representation is a valid decimal.
   */
  isValid() {
    return typeof this.bigDecimalOrInvalidGarbage !== "string";
  }

  /** Return standard decimal representation of a number, en_US encoded */
  serialize() {
    return this.bigDecimalOrInvalidGarbage.toString(); // Works for "string" types too
  }

  /**
   * Gets the internal value as a number, or NaN if the internal value is invalid. 
   */
  get numberValue() {
    if (typeof this.bigDecimalOrInvalidGarbage === "string") return Number.NaN;
    return this.bigDecimalOrInvalidGarbage.numberValue;
  }
  getFormattedValue(formatOptions) {
    return typeof this.bigDecimalOrInvalidGarbage === "string" ? this.bigDecimalOrInvalidGarbage // TODO: How to format these values?
    : ValueFormatAttributesUtils.formatNumberAsString(this.numberValue, formatOptions, !!formatOptions);
  }

  /**
   * @return true if other value equals this value
   */
  equals(other) {
    if (typeof this.bigDecimalOrInvalidGarbage === "string" || typeof other.bigDecimalOrInvalidGarbage === "string") {
      return this.bigDecimalOrInvalidGarbage === other.bigDecimalOrInvalidGarbage;
    } else {
      return this.bigDecimalOrInvalidGarbage.equals(other.bigDecimalOrInvalidGarbage);
    }
  }

  /**
   * @return -1 if this value < other value, 0 if equal, 1 if this value > other value. If either number is invalid, return -1.
   */
  compare(other) {
    if (typeof this.bigDecimalOrInvalidGarbage === "string") return -1;
    if (typeof other.bigDecimalOrInvalidGarbage === "string") return 1;
    return this.bigDecimalOrInvalidGarbage.compare(other.bigDecimalOrInvalidGarbage);
  }
}
_AbDecimal = AbDecimal;
_defineProperty(AbDecimal, "ZERO", new _AbDecimal("0"));
//# sourceMappingURL=AbDecimal.js.map
